import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import ReportPageWrapper, { StatCard } from "./ReportPageWrapper";
import {
  HandCoins,
  Landmark,
  DollarSign,
  CheckCircle,
  Clock,
  Download,
  Printer,
  Search,
  RotateCcw,
  FileText,
  PiggyBank,
} from "lucide-react";
import { format } from "date-fns";

export default function ReportsDashboard() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: summaryData, isLoading: loadingSummary } = useQuery(
    ["teacher-summary", user?.id],
    async () => {
      const { data } = await api.get(`/teachers/${user?.id}/summary`);
      return data.data || data;
    },
    { enabled: !!user?.id }
  );

  const { data: contributionsData, isLoading: loadingContrib } = useQuery(
    ["my-contributions-all", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=1000");
      return Array.isArray(data.data) ? data.data : [];
    },
    { enabled: !!user?.id }
  );

  const { data: memberPaymentsData } = useQuery(
    ["my-reports-payments", user?.id],
    async () => {
      const { data } = await api.get("/member-payments/my");
      return (data.data || []) as any[];
    },
    { enabled: !!user?.id }
  );

  const approvedSavingsAmount = useMemo(() => {
    return (memberPaymentsData || [])
      .filter((p: any) => p.type === "saving" && String(p.status || "").toLowerCase() === "approved")
      .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
  }, [memberPaymentsData]);

  const { data: loansData } = useQuery(
    ["my-loans", user?.id],
    async () => {
      const { data: res } = await api.get(`/teachers/${user?.id}/loans`);
      const d = res.data || res;
      return {
        loans: Array.isArray(d.loans) ? d.loans : [],
        totalBorrowed: d.totalBorrowed || 0,
        totalRepaid: d.totalRepaid || 0,
        outstandingBalance: d.outstandingBalance || 0,
      };
    },
    { enabled: !!user?.id }
  );

  const contributions = contributionsData || [];
  const loans = loansData?.loans || [];
  const totalBorrowed = loansData?.totalBorrowed || 0;
  const totalRepaid = loansData?.totalRepaid || 0;
  const outstandingBalance = loansData?.outstandingBalance || 0;
  const totalSavings = summaryData?.totalSavings ||
    contributions.reduce((sum: number, c: any) => sum + (parseFloat(c.amount || c.depositAmount) || 0), 0) + approvedSavingsAmount;
  const totalContributions = totalSavings;
  const currentBalance = totalSavings - outstandingBalance;
  const pendingApplications = loans.filter((l: any) => l.status === "Pending").length;

  // Build activity feed
  const activityFeed = useMemo(() => {
    const items: any[] = [];
    contributions.forEach((c: any) => {
      items.push({
        date: new Date(c.contributionDate || c.createdAt),
        type: "savings",
        description: `Savings of ${(parseFloat(c.amount || c.depositAmount) || 0).toLocaleString()} FRW`,
        status: c.isVerified ? "Verified" : "Pending",
        reference: c.referenceNumber || "N/A",
      });
    });
    loans.forEach((l: any) => {
      items.push({
        date: new Date(l.applicationDate || l.createdAt),
        type: "loan",
        description: `Loan application of ${(l.amount || 0).toLocaleString()} FRW`,
        status: l.status,
        reference: `LOAN-${l.id}`,
      });
      if (l.payments) {
        l.payments.forEach((p: any) => {
          items.push({
            date: new Date(p.paymentDate || p.createdAt),
            type: "repayment",
            description: `Repayment of ${(parseFloat(p.amount) || 0).toLocaleString()} FRW`,
            status: p.isVerified ? "Verified" : "Pending",
            reference: p.referenceNumber || "N/A",
          });
        });
      }
    });
    items.sort((a, b) => b.date.getTime() - a.date.getTime());
    return items;
  }, [contributions, loans]);

  const filtered = useMemo(() => {
    return activityFeed.filter((item: any) => {
      const matchType = typeFilter === "all" || item.type === typeFilter;
      const matchStatus = statusFilter === "all" || item.status.toLowerCase() === statusFilter.toLowerCase();
      const matchSearch = !search ||
        item.description.toLowerCase().includes(search.toLowerCase()) ||
        item.reference.toLowerCase().includes(search.toLowerCase());
      return matchType && matchStatus && matchSearch;
    });
  }, [activityFeed, typeFilter, statusFilter, search]);

  const formatCurrency = (val: number) => (val || 0).toLocaleString() + " FRW";
  const isLoading = loadingSummary || loadingContrib;

  const handleReset = () => { setSearch(""); setTypeFilter("all"); setStatusFilter("all"); };

  const handleExport = () => {
    const headers = ["Date", "Description", "Type", "Status", "Reference"];
    const rows = filtered.map((item: any) => [
      format(item.date, "yyyy-MM-dd"),
      item.description, item.type, item.status, item.reference,
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `dashboard-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl" />)}
        </div>
        <div className="h-64 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  return (
    <ReportPageWrapper
      title="Dashboard Report"
      breadcrumbItems={[
        { label: "Dashboard", path: "/member/dashboard" },
        { label: "Member Reports", path: "/member/reports" },
        { label: "Dashboard Report" },
      ]}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Total Savings" value={formatCurrency(totalSavings)} icon={PiggyBank} color="#009A44" />
        <StatCard label="Total Contributions" value={formatCurrency(totalContributions)} icon={HandCoins} color="#0EA5E9" />
        <StatCard label="Total Loans" value={formatCurrency(totalBorrowed)} icon={Landmark} color="#EAB308" />
        <StatCard label="Total Repaid" value={formatCurrency(totalRepaid)} icon={CheckCircle} color="#009A44" />
        <StatCard label="Current Balance" value={formatCurrency(currentBalance)} icon={DollarSign} color="#3B82F6" />
        <StatCard label="Pending Applications" value={pendingApplications.toString()} icon={Clock} color="#F59E0B" />
      </div>

      {/* Activity Table Card */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search activity..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white text-slate-600"
            >
              <option value="all">All Types</option>
              <option value="savings">Savings</option>
              <option value="loan">Loans</option>
              <option value="repayment">Repayments</option>
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white text-slate-600"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-slate-500 h-9">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="h-9">
              <Printer className="h-3.5 w-3.5 mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="h-9">
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#009A44]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Description</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Type</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Reference</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.slice(0, 50).map((item: any, idx: number) => {
                const amt = parseFloat(item.description.match(/[\d,]+/)?.[0]?.replace(/,/g, "") || "0");
                return (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {format(item.date, "MMM dd, yyyy")}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{item.description}</td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={item.type === "savings" ? "success" : item.type === "loan" ? "warning" : "secondary"}
                        className="text-[10px] capitalize"
                      >
                        {item.type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">
                      {amt > 0 ? amt.toLocaleString() + " FRW" : "-"}
                    </td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs max-w-[100px] truncate">
                      {item.reference}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={
                          item.status === "Verified" || item.status === "Completed" || item.status === "Active"
                            ? "success"
                            : item.status === "Pending"
                            ? "secondary"
                            : "warning"
                        }
                        className="text-[10px]"
                      >
                        {item.status}
                      </Badge>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                    No activity records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ReportPageWrapper>
  );
}
